import http from '@/api/http'

export type BookingStatus = 'CONFIRMED' | 'CANCELLED' | 'COMPLETED'

export interface ScheduleBooking {
  // One booking as returned inside `BookingTypeScheduleRspDTO.bookings`.

  id: string
  title: string | null
  start_time: string
  // ISO 8601 datetime (timezone-aware).
  end_time: string
  // ISO 8601 datetime (timezone-aware).
  status: BookingStatus
  person_id: string
  // Who the booking is for; compare against the logged-in person id to decide
  // "own" (detailed) vs "other" (blind — see `docs/sketches`) rendering.
  business_service_id: string | null
  group_id: string | null
}

export interface BookingTypeSchedule {
  // Response payload for GET `/bookings/booking-types/{id}/schedule`.

  booking_type_id: string
  booking_type_name: string | null
  start: string
  end: string
  bookings: ScheduleBooking[]
}

/**
 * Fetches all bookings for a booking type within `[start, end)`, used to
 * render the weekly schedule grid. Backend returns full booking detail
 * (including `person_id`) for every booking in range regardless of who is
 * asking — "own vs blind" is a frontend-only distinction, not enforced by
 * the API.
 */
export async function fetchBookingTypeSchedule(
  bookingTypeId: string,
  start: string,
  end: string,
): Promise<BookingTypeSchedule> {
  const { data } = await http.get<BookingTypeSchedule>(
    `/bookings/booking-types/${bookingTypeId}/schedule`,
    { params: { start, end } },
  )
  if (import.meta.env.DEV) {
    console.log(
      `[fetchBookingTypeSchedule] GET /bookings/booking-types/${bookingTypeId}/schedule response`,
      data,
    )
  }
  return data
}

export interface CreateBookingPayload {
  // Request body for POST `/bookings`. `status` is not accepted here — the
  // backend always creates a booking as `CONFIRMED`.

  booking_type_id: string
  person_id: string
  business_service_id?: string | null
  group_id?: string | null
  title?: string | null
  start_time: string
  // ISO 8601 datetime (timezone-aware).
  end_time: string
  // ISO 8601 datetime (timezone-aware).
  description?: string | null
}

export interface Booking extends CreateBookingPayload {
  id: string
  status: BookingStatus
}

/**
 * Creates a new booking via POST `/bookings`. The backend enforces
 * non-overlap per booking type at the database level (rejects with a
 * conflict if the range overlaps an existing non-cancelled booking).
 */
export async function createBooking(payload: CreateBookingPayload): Promise<Booking> {
  const { data } = await http.post<Booking>('/bookings', payload)
  if (import.meta.env.DEV) {
    console.log('[createBooking] POST /bookings response', data)
  }
  return data
}

/**
 * Cancels a booking via DELETE `/bookings/{id}`. This is a soft cancel on
 * the backend (sets `status = CANCELLED`), not a row delete — the booking
 * still exists but frees up its slot.
 */
export async function cancelBooking(id: string): Promise<void> {
  await http.delete(`/bookings/${id}`)
  if (import.meta.env.DEV) {
    console.log(`[cancelBooking] DELETE /bookings/${id}`)
  }
}

export interface UpdateBookingPayload {
  // Request body for PATCH `/bookings/{id}`. Every field is optional — the
  // backend requires at least one non-null field, but never all of them.

  booking_type_id?: string | null
  person_id?: string | null
  business_service_id?: string | null
  group_id?: string | null
  title?: string | null
  start_time?: string | null
  // ISO 8601 datetime (timezone-aware).
  end_time?: string | null
  // ISO 8601 datetime (timezone-aware).
  description?: string | null
  status?: BookingStatus | null
}

/**
 * Updates a booking via PATCH `/bookings/{id}` — used to move a booking to a
 * new time range or edit its reason. The backend re-runs the same overlap
 * check as create, so moving a booking on top of another non-cancelled one
 * (own or someone else's) is rejected the same way.
 */
export async function updateBooking(id: string, payload: UpdateBookingPayload): Promise<Booking> {
  const { data } = await http.patch<Booking>(`/bookings/${id}`, payload)
  if (import.meta.env.DEV) {
    console.log(`[updateBooking] PATCH /bookings/${id} response`, data)
  }
  return data
}
